import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import {
  Box,
  Typography,
  Card,
  Button,
  Avatar,
  useTheme,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Badge,
  Fade,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  Warning,
  StickyNote2 as NoteIcon,
  Visibility,
  VisibilityOff,
  AccessTime,
  Schedule,
  Timer,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Speed as SpeedIcon,
  ArrowDownward,
} from "@mui/icons-material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PersonIcon from "@mui/icons-material/Person";
import MockNoteCompletion from "./MockNoteCompletion";
import MockMcq from "./MockMcq";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";
import InputText from "../../../components/Input/InputText";

/* ---------------------------------------------------------------
   Utils
--------------------------------------------------------------- */

const BASE_URL = "https://api.mmitsol.com/";

const pad2 = (n) => String(n).padStart(2, "0");
const fmtClock = (s) => {
  if (s == null) return "--:--:--";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
};

function normalizeHeading(raw) {
  if (!raw) return "";
  const parts = String(raw)
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const joined = parts.join(" – ");
  return joined.replace(/\s{2,}/g, " ").trim();
}

function cleanQuestionText(raw = "") {
  return String(raw).replace(/\s+/g, " ").trim();
}

const hasAnswer = (answers, id) =>
  id != null && String(answers?.[id] ?? "").trim() !== "";

/* ---------------------------------------------------------------
   Data Normalizers for Playlist Construction
--------------------------------------------------------------- */

function normalizeLegacyPartAExtract(extract) {
  // Case 1: aggregated sections wrapper (current parent output)
  const maybeSections = extract?.questions?.[0]?.content?.sections;
  if (Array.isArray(maybeSections) && maybeSections.length) {
    const sections = maybeSections.map((sec) => {
      const notes = (sec?.notes || []).map((n) => {
        const qid = n?.questionId ?? n?.question_id ?? null;
        const rawTxt = n?.text ?? n?.question ?? "";
        const txt = cleanQuestionText(rawTxt);
        const inferBlank =
          qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);
        const hasBlank = n?.hasBlank != null ? !!n.hasBlank : inferBlank;
        return {
          questionId: qid,
          text: txt,
          hasBlank,
        };
      });
      return {
        heading: normalizeHeading(sec?.heading),
        notes,
      };
    });

    const questionIds = [];
    sections.forEach((s) =>
      s.notes.forEach((n) => {
        if (n.questionId != null) questionIds.push(Number(n.questionId));
      }),
    );

    // Patient name from Patient section first note that has no blank
    let patientName = "";
    const patientSec = sections.find((s) => /^patient\b/i.test(s.heading));
    if (patientSec?.notes?.length) {
      const nameNote =
        patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
        patientSec.notes.find((n) => n.text?.trim()) ||
        patientSec.notes[0];
      patientName = (nameNote?.text || "").trim();
    }

    return { sections, questionIds, patientName };
  }

  // Case 2: per-question content array fallback
  const qArr = extract?.questions?.map((q) => q?.content) || [];
  const byHeading = new Map();
  qArr.forEach((n) => {
    const heading = normalizeHeading(n?.heading);
    if (!byHeading.has(heading)) byHeading.set(heading, []);
    byHeading.get(heading).push({
      questionId: n?.questionId ?? null,
      text: cleanQuestionText(n?.question ?? ""),
      hasBlank: n?.questionId != null,
    });
  });
  const sections = Array.from(byHeading.entries()).map(([heading, notes]) => ({
    heading,
    notes,
  }));
  const questionIds = qArr
    .map((n) => n?.questionId)
    .filter((v) => v != null)
    .map(Number);
  let patientName = "";
  const patientSec = sections.find((s) => /^patient\b/i.test(s.heading));
  if (patientSec?.notes?.length) {
    patientName = patientSec.notes[0].text;
  }
  return { sections, questionIds, patientName };
}

function normalizeLegacyPartBExtract(extract) {
  return (
    extract?.questions?.map((qWrap, idx) => {
      const q = qWrap?.content || {};
      return {
        part: "B",
        type: "mcqSingle",
        audioURL: extract?.audioURL || null,
        extractName:
          extract?.extractName ||
          `Extract ${idx + 1} – Question ${q?.questionId ?? idx + 1}`,
        instructions: extract?.instructions || "",
        content: {
          questionId: q?.questionId,
          question_id: q?.question_id,
          question: q?.question || "",
          options: q?.options || [],
        },
      };
    }) || []
  );
}

function normalizeLegacyPartCExtract(extract) {
  const contents =
    extract?.questions?.map((qWrap) => {
      const q = qWrap?.content || {};
      return {
        questionId: q?.questionId,
        question_id: q?.question_id,
        question: q?.question || "",
        options: q?.options || [],
      };
    }) || [];
  const questionIds = contents
    .map((c) => c?.questionId)
    .filter((v) => v != null)
    .map(Number);
  return {
    part: "C",
    type: "mcqGroup",
    audioURL: extract?.audioURL || null,
    extractName: extract?.extractName || "",
    instructions: extract?.instructions || "",
    contents,
    questionIds,
  };
}

function buildPlaylist(listeningData = {}) {
  const playlist = [];

  // Handle new API format with listeningSections (direct questions array)
  if (
    listeningData?.listeningSections &&
    Array.isArray(listeningData.listeningSections)
  ) {
    listeningData.listeningSections.forEach((section, sectionIndex) => {
      const questions = section.questions || [];

      // Group questions by their explicit part (question.part) when available,
      // otherwise fall back to section.part or default to A.
      const byPart = { A: [], B: [], C: [] };
      questions.forEach((q) => {
        const p = (q.part || section.part || "A").toUpperCase();
        if (p === "B") byPart.B.push(q);
        else if (p === "C") byPart.C.push(q);
        else byPart.A.push(q);
      });

      // If the section contains mixed parts (A + B/C), create a composite screen
      // that shows Part A note completion followed by any Part B single MCQs
      // and Part C grouped MCQs. This keeps the UI on a single page per section
      // which matches the desired layout for the newer API shape.
      const hasA = byPart.A.length > 0;
      const hasB = byPart.B.length > 0;
      const hasC = byPart.C.length > 0;

      if (hasA || hasB || hasC) {
        const questionIds = [];

        // Extract instructions from first question (all questions in section share same instructions)
        const sectionInstructions =
          questions[0]?.instructions || section.instructions || "";

        // Helper function to get best instruction for each question type
        // Prefer instructions with HTML formatting (like <b> tags) from first occurrence
        const getInstructionByType = (questionType) => {
          const sameTypeQuestions = questions.filter(q => {
            const qType = (q.questionType || q.type || "").toLowerCase();
            const targetType = (questionType || "").toLowerCase();
            return qType === targetType || qType.includes(targetType) || targetType.includes(qType);
          });
          
          // Find first question with HTML-formatted instructions (contains < or >)
          const withHtml = sameTypeQuestions.find(q => 
            q.instructions && (q.instructions.includes('<') || q.instructions.includes('>'))
          );
          
          if (withHtml) return withHtml.instructions;
          
          // Otherwise return first available instruction
          return sameTypeQuestions.find(q => q.instructions)?.instructions || "";
        };

        // Build an ordered items array from original questions so we preserve sequence
        const items = questions.map((question) => {
          const qid = question.questionId ?? question.question_id ?? null;
          if (qid != null) questionIds.push(Number(qid));

          const qType = (
            question.questionType ||
            question.type ||
            ""
          ).toLowerCase();

          // Handle flowchart_completion_select questions (dropdown-based flowchart)
          if (
            qType === "flowchart_completion_select" ||
            question.questionType === "flowchart_completion_select"
          ) {
            return {
              type: "flowchart_completion_select",
              questionId: qid,
              question: cleanQuestionText(question.question || ""),
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionMainHeading: question.questionMainHeading || "",
              questionType: "flowchart_completion_select",
              instructions: getInstructionByType("flowchart_completion_select"),
              features: question.features || "",
            };
          }

          // Handle flowchart_completion questions (text input-based flowchart)
          if (
            qType === "flowchart_completion" ||
            question.questionType === "flowchart_completion"
          ) {
            return {
              type: "flowchart_completion",
              questionId: qid,
              question: cleanQuestionText(question.question || ""),
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionMainHeading: question.questionMainHeading || "",
              questionType: "flowchart_completion",
              instructions: getInstructionByType("flowchart_completion"),
              features: question.features || "",
            };
          }

          if (
            qType.includes("note") ||
            qType.includes("notes") ||
            qType.includes("completion") ||
            qType === "short_question_answers" ||
            qType === "short_answer_questions" ||
            question.questionType === "notes_completion" ||
            question.questionType === "form_completion" ||
            question.questionType === "table_completion" ||
            question.questionType === "summary_completion" ||
            question.questionType === "sentence_completion" ||
            question.questionType === "short_question_answers" ||
            question.questionType === "flow_chart_completion" ||
            question.questionType === "diagram_completion"
          ) {
            return {
              type: "note",
              questionId: qid,
              question: cleanQuestionText(question.question || ""),
              heading:
                question.questionHeading ||
                question.heading ||
                question.questionMainHeading ||
                "",
              questionMainHeading: question.questionMainHeading || "",
              instructions: getInstructionByType(question.questionType || "notes_completion"),
              table: question.table || null,
              questionType: question.questionType || "",
              imageUrl: question.imageUrl || null,
            };
          }

          // Handle map/diagram labelling questions (check BEFORE mcq to avoid conflict)
          if (
            qType === "map_labelling" ||
            qType === "diagram_labelling" ||
            question.questionType === "map_labelling" ||
            question.questionType === "diagram_labelling"
          ) {
            const imgUrl = question.imageUrl || "";
            const isDiagramLabelling =
              qType === "diagram_labelling" ||
              question.questionType === "diagram_labelling";
            return {
              type: "map_labelling",
              questionId: qid,
              question_id: qid,
              question: question.question || "",
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionMainHeading: question.questionMainHeading || "",
              imageUrl: imgUrl,
              questionType: isDiagramLabelling
                ? "diagram_labelling"
                : "map_labelling",
              instructions: isDiagramLabelling
                ? getInstructionByType("diagram_labelling") ||
                  getInstructionByType("map_labelling")
                : getInstructionByType("map_labelling"),
            };
          }

          // Handle matching_information questions (check BEFORE mcq to avoid conflict)
          if (
            qType === "matching_information" ||
            question.questionType === "matching_information"
          ) {
            return {
              type: "matching_information",
              questionId: qid,
              question_id: qid,
              question: question.question || "",
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionMainHeading: question.questionMainHeading || "",
              questionType: "matching_information",
              instructions: getInstructionByType("matching_information"),
              features: question.features || "",
            };
          }

          // Handle matching_feature questions (check BEFORE mcq to avoid conflict)
          if (
            qType === "matching_feature" ||
            question.questionType === "matching_feature"
          ) {
            return {
              type: "matching_feature",
              questionId: qid,
              question_id: qid,
              question: question.question || "",
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionMainHeading: question.questionMainHeading || "",
              questionType: "matching_feature",
              instructions: getInstructionByType("matching_feature"),
              features: question.features || "",
            };
          }

          if (
            qType.includes("mcq") ||
            qType.includes("multiple") ||
            qType.includes("single") ||
            question.options
          ) {
            const isMulti =
              /muti|multi|multiple/i.test(question.questionType || "") ||
              question.questionType === "mcq_mutiple" ||
              question.questionType === "mcq_multiple";
            return {
              type: isMulti ? "mcq_mutiple" : "mcq_single",
              questionId: qid,
              question_id: qid,
              question: question.question || "",
              options: question.options || [],
              heading: question.questionHeading || question.heading || "",
              questionNumber: question.questionNumber || "",
              questionRange: question.questionRange || "",
              questionType:
                question.questionType ||
                (isMulti ? "mcq_mutiple" : "mcq_single"),
              instructions: getInstructionByType(question.questionType || (isMulti ? "mcq_multiple" : "mcq_single")),
            };
          }

          // Fallback generic item
          return {
            type: question.questionType || "unknown",
            questionId: qid,
            question: question.question || "",
            heading: question.questionHeading || question.heading || "",
            options: question.options || [],
          };
        });

        // Group mcq_multiple questions by questionRange while preserving original order
        // We track the original index of each item so we can maintain proper sequence
        const mcqMultipleByRange = new Map();
        const mcqMultipleFirstIndex = new Map(); // Track original index of first question in each group
        const allItemsWithIndex = []; // Store all items with their original index

        questions.forEach((question, idx) => {
          const item = items[idx];
          const qType = (question.questionType || "").toLowerCase();

          if (qType === "mcq_multiple") {
            // Create a grouping key based on questionRange or question text + options
            const rangeKey = question.questionRange || "";
            const questionKey = `${question.question || ""}_${JSON.stringify(question.options || [])}`;
            const groupKey = rangeKey || questionKey;

            if (!mcqMultipleByRange.has(groupKey)) {
              mcqMultipleByRange.set(groupKey, []);
              mcqMultipleFirstIndex.set(groupKey, idx); // Store original index of first in group
            }
            mcqMultipleByRange
              .get(groupKey)
              .push({ item, question, originalIndex: idx });
          } else {
            // Non-mcq_multiple items keep their original index
            allItemsWithIndex.push({ item, originalIndex: idx });
          }
        });

        // Add grouped mcq_multiple items at their proper position (first question's index)
        mcqMultipleByRange.forEach((group, groupKey) => {
          if (group.length > 0) {
            const firstOriginalIdx = mcqMultipleFirstIndex.get(groupKey);

            if (group.length > 1) {
              // Multiple questions with same range - create grouped item
              const firstItem = group[0].item;
              allItemsWithIndex.push({
                originalIndex: firstOriginalIdx,
                item: {
                  ...firstItem,
                  isGrouped: true,
                  questionIds: group.map((g) => g.item.questionId),
                  groupedQuestions: group.map((g) => g.question),
                  type: "mcq_single", // Treat as single MCQ with multiple selections
                },
              });
            } else {
              // Single mcq_multiple question - add as normal
              allItemsWithIndex.push({
                originalIndex: firstOriginalIdx,
                item: group[0].item,
              });
            }
          }
        });

        // Sort ALL items by their original index to maintain proper order
        allItemsWithIndex.sort((a, b) => a.originalIndex - b.originalIndex);

        // Extract just the items (without the index wrapper)
        const groupedItems = allItemsWithIndex.map((entry) => entry.item);

        playlist.push({
          part: section.part || (hasA ? "A" : hasB ? "B" : "C"),
          type: "composite",
          audioURL: section.audioUrl || null,
          extractName: section.title || `Section ${sectionIndex + 1}`,
          instructions: sectionInstructions,
          content: {
            items: groupedItems,
          },
          questionIds,
        });
      }
    });

    return playlist;
  }

  // Handle new API format with listeningParts
  if (
    listeningData?.listeningParts &&
    Array.isArray(listeningData.listeningParts)
  ) {
    listeningData.listeningParts.forEach((part) => {
      const partType = part.part?.toUpperCase();

      if (partType === "A" && part.listeningExtract) {
        // Part A: Note completion - group all questions by extract
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            // Group questions by heading to create sections
            const byHeading = new Map();
            const questionIds = [];

            extract.extractQuestion.forEach((question) => {
              const heading = normalizeHeading(question.heading);
              if (!byHeading.has(heading)) byHeading.set(heading, []);

              const qid = question.questionId;
              const rawTxt = question.question || "";
              const txt = cleanQuestionText(rawTxt);
              const inferBlank =
                qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);
              const hasBlank = inferBlank;

              byHeading.get(heading).push({
                questionId: qid,
                text: txt,
                hasBlank,
              });

              if (qid != null) {
                questionIds.push(Number(qid));
              }
            });

            const sections = Array.from(byHeading.entries()).map(
              ([heading, notes]) => ({
                heading,
                notes,
              }),
            );

            // Find patient name from Patient section
            let patientName = "";
            const patientSec = sections.find((s) =>
              /^patient\b/i.test(s.heading),
            );
            if (patientSec?.notes?.length) {
              const nameNote =
                patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
                patientSec.notes.find((n) => n.text?.trim()) ||
                patientSec.notes[0];
              patientName = (nameNote?.text || "").trim();
            }

            playlist.push({
              part: "A",
              type: "noteCompletion",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              content: { sections },
              questionIds,
              patientName,
            });
          }
        });
      }

      if (partType === "B" && part.listeningExtract) {
        // Part B: Each extract has one question - create separate screens
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            extract.extractQuestion.forEach((question, questionIndex) => {
              playlist.push({
                part: "B",
                type: "mcqSingle",
                audioURL: extract.audioUrl || null,
                extractName:
                  extract.extractName || `Extract ${extractIndex + 1}`,
                instructions: extract.instructions || "",
                content: {
                  questionId: question.questionId,
                  question_id: question.questionId,
                  question: question.question || "",
                  options: question.options || [],
                  heading: question.heading || "",
                  questionNumber: question.questionNumber || "",
                  questionType: question.questionType || "mcq",
                },
              });
            });
          }
        });
      }

      if (partType === "C" && part.listeningExtract) {
        // Part C: Multiple choice group - group all questions per extract
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            const contents = extract.extractQuestion.map((question) => ({
              questionId: question.questionId,
              question_id: question.questionId,
              question: question.question || "",
              options: question.options || [],
              heading: question.heading || "",
              questionNumber: question.questionNumber || "",
              questionType: question.questionType || "mcq",
            }));

            const questionIds = contents
              .map((c) => c?.questionId)
              .filter((v) => v != null)
              .map(Number);

            playlist.push({
              part: "C",
              type: "mcqGroup",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              contents,
              questionIds,
            });
          }
        });
      }
    });

    return playlist;
  }

  // Fallback to legacy format
  const partA = listeningData?.partA;
  const partB = listeningData?.partB;
  const partC = listeningData?.partC;

  // Part A
  partA?.extracts?.forEach((ex) => {
    const norm = normalizeLegacyPartAExtract(ex);
    playlist.push({
      part: "A",
      type: "noteCompletion",
      audioURL: ex?.audioURL || null,
      extractName: ex?.extractName || cleanQuestionText(ex.instructions),
      instructions: ex?.instructions || "",
      content: { sections: norm.sections },
      questionIds: norm.questionIds,
      patientName: norm.patientName,
    });
  });

  // Part B
  partB?.extracts?.forEach((ex) => {
    normalizeLegacyPartBExtract(ex).forEach((screen) => playlist.push(screen));
  });

  // Part C
  partC?.extracts?.forEach((ex) => {
    playlist.push(normalizeLegacyPartCExtract(ex));
  });

  return playlist;
}

/* ---------------------------------------------------------------
   Header helpers
--------------------------------------------------------------- */

function getHeaderQuestionIds(screen, playlist) {
  if (!screen) return [];
  if (screen.part === "A") return screen.questionIds || [];
  if (screen.part === "B") {
    // For Part B, return all Part B question IDs for chip display
    const allPartBQuestions = playlist
      .filter((item) => item.part === "B")
      .map((item) => {
        const id = item.content?.questionId ?? item.content?.question_id;
        return id != null ? Number(id) : null;
      })
      .filter((id) => id != null);

    return allPartBQuestions;
  }
  if (screen.part === "C") return screen.questionIds || [];
  return [];
}

function getExtractProgress(screen, playlist) {
  if (!screen) return { idx: 0, total: 0 };
  const part = screen.part;
  const inPart = playlist.filter((p) => p.part === part);
  const globalIdx = playlist.indexOf(screen);
  const idx = inPart.findIndex((p) => playlist.indexOf(p) === globalIdx);
  return { idx: idx >= 0 ? idx : 0, total: inPart.length };
}

/* ---------------------------------------------------------------
   ListeningSection Component
--------------------------------------------------------------- */

export default function ListeningSection({
  listeningData,
  listenScreen,
  setListenScreen,
  audioRef,
  instrOpen,
  answers,
  onAnswerChange,
  onAutoNext,
  timer,
  mode = "simulation",
  currentPart = "A",
  onBack = null,
  isLastQuestion = false,
  isLastModuleInSimulation = false, // Whether this is the last module in simulation mode (no reading/writing/speaking after)
  isLastQuestionInSimulation = false, // Whether this is the last question/part in simulation mode
  onFinish = null,
  testId = null, // Test ID for browser back navigation
  bundleId = null, // Bundle ID for browser back navigation (if in bundle mode)
  // Notes props - now managed by parent component
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
}) {
  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();

  // Playback speed state (only for practice mode)
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const speedMenuOpen = Boolean(speedMenuAnchor);

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Detect test type from URL
      const path = location.pathname.toLowerCase();
      let backPath;
      if (bundleId) {
        backPath = `/app/bundles/${bundleId}`;
      } else if (path.includes('/mini-mock-test/')) {
        backPath = `/mini-mock-test/${testId}`;
      } else if (path.includes('/class-test/')) {
        backPath = `/class-test/${testId}`;
      } else {
        backPath = `/mock-test/${testId}`;
      }
      navigate(backPath, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, bundleId, navigate]);

  // Track previous instrOpen to detect close event
  const prevInstrOpenRef = useRef(instrOpen);

  // Pause and resume audio when instruction modal opens/closes
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    if (instrOpen) {
      audio.pause();
    } else if (prevInstrOpenRef.current && !instrOpen) {
      setTimeout(() => {
        if (audio && !audio.ended) {
          audio.play().catch(() => {});
        }
      }, 100);
    }
    prevInstrOpenRef.current = instrOpen;
  }, [mode, instrOpen, audioRef]);

  // Ensure audio elements are synchronized between desktop and mobile views
  useEffect(() => {
    // Find all audio elements and ensure the visible one is referenced
    const handleResize = () => {
      const desktopAudio = document.querySelector(
        'audio[data-audio-type="desktop"]',
      );
      const mobileAudio = document.querySelector(
        'audio[data-audio-type="mobile"]',
      );

      if (
        window.innerWidth >= 960 &&
        desktopAudio &&
        desktopAudio.style.display !== "none"
      ) {
        audioRef.current = desktopAudio;
      } else if (
        window.innerWidth < 960 &&
        mobileAudio &&
        mobileAudio.style.display !== "none"
      ) {
        audioRef.current = mobileAudio;
      }
    };

    handleResize(); // Initial assignment
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [audioRef]);

  // Apply playback speed to audio element (only in practice mode)
  useEffect(() => {
    if (mode === "practice" && audioRef?.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, audioRef, mode]);

  const theme = useTheme();
  const divider = theme.palette.divider;
  const bgPaper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const border = theme.palette.divider;

  /* Build playlist from listening data */
  const playlist = useMemo(() => {
    console.log(
      "[DEBUG ListeningSection] Building playlist from listeningData:",
      listeningData,
    );
    // If listeningData has partA/partB/partC (transformed format), use it directly
    // Otherwise, try to build from raw format
    if (listeningData?.partA || listeningData?.partB || listeningData?.partC) {
      console.log(
        "[DEBUG ListeningSection] Using transformed format (partA/B/C)",
      );
      return buildPlaylist(listeningData);
    }
    // If it's raw API format with listeningSections, build playlist from it
    if (listeningData?.listeningSections) {
      console.log(
        "[DEBUG ListeningSection] Using raw API format (listeningSections)",
      );
      console.log(
        "[DEBUG ListeningSection] listeningSections:",
        listeningData.listeningSections,
      );
      const result = buildPlaylist(listeningData);
      console.log("[DEBUG ListeningSection] Playlist built:", result);
      return result;
    }
    console.log(
      "[DEBUG ListeningSection] No valid format found, returning empty playlist",
    );
    return [];
  }, [listeningData]);
  const screen = playlist[listenScreen] || null;
  console.log(
    "[DEBUG ListeningSection] Current screen (listenScreen=" +
      listenScreen +
      "):",
    screen,
  );

  // Scroll to top when screen changes (moving to next extract/part)
  useEffect(() => {
    const mainContent = document.querySelector(".listening-main-content");
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [listenScreen]);

  // Part A: filter out the "Patient" section; build summary bullets
  const partAFiltered = useMemo(() => {
    if (!screen || screen.part !== "A") {
      console.log("[DEBUG partAFiltered] Not Part A or no screen");
      return { sections: [], summary: null, questionIds: [] };
    }
    const allSecs = screen.content?.sections || [];
    console.log("[DEBUG partAFiltered] screen.content:", screen.content);
    console.log("[DEBUG partAFiltered] allSecs:", allSecs);

    const filteredSecs = allSecs.filter((s) => {
      const heading = (s.heading || "").trim();
      // Only filter out sections that are exactly "Patient" (case insensitive)
      return !/^patient$/i.test(heading);
    });

    console.log("[DEBUG partAFiltered] filteredSecs:", filteredSecs);

    // Collect all question IDs from filtered sections
    const questionIds = [];
    filteredSecs.forEach((sec) => {
      sec.notes?.forEach((note) => {
        if (note.questionId != null) {
          questionIds.push(Number(note.questionId));
        }
      });
    });

    // Summary: use the first non-Patient section
    const firstSec = filteredSecs[0];
    let summary = null;
    if (firstSec) {
      const staticLines = (firstSec.notes || [])
        .filter((n) => n.questionId == null && n.text?.trim())
        .slice(0, 2)
        .map((n) => n.text.trim());
      summary = {
        heading: firstSec.heading,
        lines: staticLines,
      };
    }
    console.log("[DEBUG partAFiltered] Final result:", {
      sections: filteredSecs,
      summary,
      questionIds,
    });
    return { sections: filteredSecs, summary, questionIds };
  }, [screen]);

  // Extract progress pill
  const { idx: partIdx, total: partTotal } = useMemo(
    () => getExtractProgress(screen, playlist),
    [screen, playlist],
  );

  // Question chips
  const headerQuestionIds = useMemo(
    () => getHeaderQuestionIds(screen, playlist),
    [screen, playlist],
  );

  // Audio timers
  const [audioDur, setAudioDur] = useState(0);
  const [audioPos, setAudioPos] = useState(0);

  // Audio-based timer state (adjusted for playback speed)
  const [audioTimeRemaining, setAudioTimeRemaining] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false); // Start running, pause only when audio is paused
  const [audioStarted, setAudioStarted] = useState(false); // Track if audio has started playing

  // Timer expired modal state
  const [timerExpired, setTimerExpired] = useState(false);
  const [expiryCountdown, setExpiryCountdown] = useState(10);
  const [expiryHandled, setExpiryHandled] = useState(false);
  const [stayHereUsed, setStayHereUsed] = useState(false);
  const [timeExtension, setTimeExtension] = useState(0); // Track extended time in seconds

  // Extended timer for "Stay Here" functionality
  const [showExtendedTimer, setShowExtendedTimer] = useState(false);
  const [extendedCountdown, setExtendedCountdown] = useState(10);

  // Navigation confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Mouse position tracking for auto-hide header
  // Add a ref to hold the auto-advance timeout
  const autoAdvanceTimeoutRef = useRef(null);
  const extendedTimerRef = useRef(null);

  // Wrapper to track all setListenScreen calls
  const trackedSetListenScreen = useCallback(
    (value) => {
      console.log(
        "🚨 setListenScreen called with:",
        value,
        "Current screen:",
        listenScreen,
      );
      console.trace("Call stack:");
      if (typeof value === "function") {
        setListenScreen((prev) => {
          const newValue = value(prev);
          console.log("🚨 setListenScreen changing from", prev, "to", newValue);
          return newValue;
        });
      } else {
        console.log("🚨 setListenScreen changing to", value);
        setListenScreen(value);
      }
    },
    [listenScreen, setListenScreen],
  );

  // Handle timer expiry - in practice mode, do nothing; in simulation, move as before
  const handleTimerExpire = useCallback(() => {
    console.log(
      "handleTimerExpire called - expiryHandled:",
      expiryHandled,
      "showExtendedTimer:",
      showExtendedTimer,
    );

    if (!expiryHandled) {
      console.log("Processing timer expiry...");
      setExpiryHandled(true);
      setTimerExpired(false);

      // Practice mode: don't do anything automatically when timer expires
      // User must click Next button manually
      if (mode === "practice") {
        console.log("Practice mode - not auto-navigating");
        // Don't automatically finish or navigate - let user click Next
      } else {
        console.log("Simulation mode - auto-navigating...");
        // Simulation mode: auto-navigate as before
        // Check if there's a next extract in the current part
        if (listenScreen < playlist.length - 1) {
          console.log("Moving to next extract via handleTimerExpire");
          // Move to next extract
          trackedSetListenScreen((prev) => prev + 1);
        } else if (isLastQuestion && onFinish) {
          console.log("Finishing test via handleTimerExpire");
          // This was the last question overall
          onFinish();
        } else if (onAutoNext) {
          console.log("Moving to next part via handleTimerExpire");
          // Move to next part
          onAutoNext();
        }
      }
    } else {
      console.log("Timer expiry ignored - already handled");
    }
  }, [
    expiryHandled,
    mode,
    isLastQuestion,
    onFinish,
    onAutoNext,
    listenScreen,
    playlist.length,
    setListenScreen,
  ]);

  // Timer effect for audio-based timing (adjusted for playback speed)
  useEffect(() => {
    // Don't start timer if instructions are currently open
    if (instrOpen) {
      return;
    }

    // For practice mode, only start if audio has started playing
    if (mode === "practice" && !audioStarted) {
      return;
    }

    // Only start timer if we have audio duration
    if (audioDur <= 0) {
      return;
    }

    const updateAudioTimer = () => {
      if (timerPaused) {
        return;
      }

      // Get audio reference and make sure it's available
      const audio = audioRef?.current;
      if (!audio) {
        return;
      }

      // Get the latest audio position directly from the audio element
      const currentPos = audio.currentTime || 0;
      
      // Calculate remaining audio time (full duration, not adjusted by speed)
      const adjustedDuration = audioDur + timeExtension;
      const remainingAudioTime = Math.max(0, adjustedDuration - currentPos);
      
      // Show full audio time remaining (e.g., 4 mins shows as 4 mins even at 2x speed)
      // The timer will count down faster because audio position advances faster at higher speeds
      const remainingSeconds = Math.floor(remainingAudioTime);

      // Update the timer display
      setAudioTimeRemaining(remainingSeconds);

      // Check for timer expiration
      if (remainingSeconds <= 0 && !timerExpired && !expiryHandled) {
        if (mode === "simulation") {
          if (stayHereUsed) {
            // If "Stay Here" was already used, auto-advance without modal
            setExpiryHandled(true);
            handleTimerExpire();
          } else {
            // Show modal with "Stay Here" option
            setTimerExpired(true);
            setExpiryHandled(false);
          }
        } else {
          // In practice mode, just mark as expired without showing modal
          setExpiryHandled(true);
        }
      }
    };

    updateAudioTimer();
    // Update more frequently (every 100ms) for smoother countdown with speed changes
    const interval = setInterval(updateAudioTimer, 100);

    const handlePauseTimer = () => setTimerPaused(true);
    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [
    audioDur,
    audioPos,
    timerPaused,
    instrOpen,
    timerExpired,
    expiryHandled,
    mode,
    audioStarted,
    timeExtension,
    stayHereUsed,
    handleTimerExpire,
    playbackSpeed, // Added playbackSpeed to dependencies
  ]);

  // Timer expiry effect: start countdown and auto-advance unless user clicks Continue
  useEffect(() => {
    if (timerExpired && !expiryHandled) {
      setExpiryCountdown(10);
      const interval = setInterval(() => {
        setExpiryCountdown((s) => {
          if (s <= 1) {
            clearInterval(interval);
            handleTimerExpire();
            return 10;
          }
          return s - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
    // If timerExpired becomes false (e.g., user clicked "Stay Here"), make sure countdown stops
  }, [timerExpired, expiryHandled, handleTimerExpire]);

  // "Continue" button handler for timer modal
  const handleTimerContinue = () => {
    if (!expiryHandled) {
      handleTimerExpire();
    }
  };

  // "Stay Here" button handler for timer modal
  const handleStayHere = useCallback(() => {
    console.log("Stay Here clicked - starting 10 second timer");

    // Close the modal and prevent all other timer logic
    setTimerExpired(false);
    setExpiryHandled(true); // This should block other timers
    setStayHereUsed(true); // Mark that Stay Here has been used
    setShowExtendedTimer(true);
    setExtendedCountdown(10);

    console.log("Modal closed, timer states set, stayHereUsed = true");

    // Clear any existing timers
    if (autoAdvanceTimeoutRef.current) {
      console.log("Clearing existing autoAdvanceTimeout");
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    if (extendedTimerRef.current) {
      console.log("Clearing existing extendedTimer");
      clearInterval(extendedTimerRef.current);
      extendedTimerRef.current = null;
    }

    console.log("Starting extended countdown...");

    // Start countdown
    let count = 10;
    extendedTimerRef.current = setInterval(() => {
      count--;
      setExtendedCountdown(count);
      console.log("Extended countdown:", count);

      if (count <= 0) {
        console.log("Countdown finished - auto-advancing to next section");
        clearInterval(extendedTimerRef.current);
        extendedTimerRef.current = null;
        setShowExtendedTimer(false);
        
        // Auto-advance without showing modal again
        console.log("Extended timer finished - moving to next section automatically");
        if (mode === "simulation") {
          handleTimerExpire();
        }
      }
    }, 1000);

    console.log("Extended timer setup complete");
  }, [mode, handleTimerExpire]);

  // Reset expiryHandled for new extract (not just new part)
  useEffect(() => {
    setExpiryHandled(false);
    setTimerExpired(false);
    setStayHereUsed(false);
    setTimeExtension(0);
    setShowExtendedTimer(false);
    setExtendedCountdown(10);

    // Clear extended timer if running
    if (extendedTimerRef.current) {
      clearInterval(extendedTimerRef.current);
    }
  }, [listenScreen]); // Changed from currentPart to listenScreen

  // Navigation confirmation handlers
  const handleNextClick = useCallback(() => {
    // For validation, use only current screen questions, not all Part B questions
    let validationIds;
    if (screen?.part === "B") {
      // For Part B, only validate the current question
      const currentId =
        screen.content?.questionId ?? screen.content?.question_id;
      validationIds = currentId != null ? [Number(currentId)] : [];
    } else {
      // For Part A and C, use all questions on current screen
      validationIds = headerQuestionIds.filter(
        (id) => id != null && !isNaN(id) && typeof id === "number",
      );
    }

    // Check each ID for answers and provide detailed debugging
    const unattemptedDetails = validationIds.map((id) => {
      const answer = answers?.[id];
      const hasAnswerResult = hasAnswer(answers, id);
      const trimmedAnswer = String(answer ?? "").trim();
      return {
        id,
        answer,
        trimmedAnswer,
        hasAnswer: hasAnswerResult,
        isEmpty: trimmedAnswer === "",
      };
    });

    const unattemptedQuestions = unattemptedDetails.filter(
      (detail) => !detail.hasAnswer,
    );
    const anyUnattempted = unattemptedQuestions.length > 0;

    // Only show dialog if there are actually unattempted questions
    if (anyUnattempted) {
      setPendingAction(() => () => {
        if (listenScreen < playlist.length - 1) {
          setListenScreen((prev) => prev + 1);
        } else if (mode === "practice" && isLastQuestion && onFinish) {
          onFinish();
        } else if (
          mode === "simulation" &&
          isLastModuleInSimulation &&
          isLastQuestionInSimulation &&
          onFinish
        ) {
          onFinish();
        } else {
          onAutoNext?.();
        }
      });
      setShowConfirm(true);
    } else {
      // All attempted: proceed without dialog
      if (listenScreen < playlist.length - 1) {
        setListenScreen((prev) => prev + 1);
      } else if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      } else if (
        mode === "simulation" &&
        isLastModuleInSimulation &&
        isLastQuestionInSimulation &&
        onFinish
      ) {
        onFinish();
      } else {
        onAutoNext?.();
      }
    }
  }, [
    listenScreen,
    playlist.length,
    setListenScreen,
    mode,
    isLastQuestion,
    isLastModuleInSimulation,
    isLastQuestionInSimulation,
    onFinish,
    onAutoNext,
    answers,
    screen,
    headerQuestionIds,
  ]);

  const handleConfirmYes = () => {
    setShowConfirm(false);
    if (pendingAction) pendingAction();
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
    setPendingAction(null);
  };

  // Handle chip click to navigate to specific question
  const handleChipClick = (questionId) => {
    // For Part B, find the screen with this question and navigate to it
    if (screen?.part === "B") {
      const targetScreenIndex = playlist.findIndex((item) => {
        const id = item.content?.questionId ?? item.content?.question_id;
        return Number(id) === questionId;
      });

      if (targetScreenIndex !== -1) {
        setListenScreen(targetScreenIndex);
        return;
      }
    }

    // For Part A and C, scroll to the question element with professional highlighting
    const element = document.querySelector(
      `[data-question-id="${questionId}"]`,
    );
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Professional highlighting effect
      const originalBackground = element.style.backgroundColor;
      const originalTransition = element.style.transition;
      const originalBoxShadow = element.style.boxShadow;

      // Apply professional highlight
      element.style.transition = "all 0.3s ease-in-out";
      element.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
      element.style.boxShadow =
        "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
      element.style.borderRadius = "8px";

      // Pulse effect
      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
        element.style.boxShadow =
          "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
      }, 150);

      // Fade out
      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
        element.style.boxShadow =
          "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
      }, 400);

      // Remove highlight
      setTimeout(() => {
        element.style.backgroundColor = originalBackground;
        element.style.boxShadow = originalBoxShadow;
        element.style.transition = originalTransition;
      }, 2000);
    }
  };

  // Function to format seconds to MM:SS
  const getDisplayTime = () => {
    // Always use audio-based timer (no more API timer)
    // For most accurate display, get time directly from audio element
    const audio = audioRef?.current;
    const currentTime = audio?.currentTime || 0;
    
    // Calculate remaining audio time (full duration)
    // Shows actual audio time left (e.g., 4 mins at any speed)
    // Timer counts down faster at higher speeds because currentTime advances faster
    const remainingAudioTime = audioDur > 0 ? Math.max(0, audioDur - currentTime) : 0;

    return fmtClock(Math.floor(remainingAudioTime));
  };

  const remaining =
    mode === "simulation" ? Math.max(audioDur - audioPos, 0) : null;

  // Only restart audio if audioURL changes
  const lastAudioURL = useRef("");
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || !screen) return;
    let src = "";
    if (screen.audioURL) {
      src = screen.audioURL.startsWith("http")
        ? screen.audioURL
        : BASE_URL + screen.audioURL;
    }
    if (src && src !== lastAudioURL.current) {
      audio.pause();
      audio.src = src;
      audio.currentTime = 0;
      if (!instrOpen) {
        audio.play().catch(() => {});
      }
      setAudioPos(0);
      setAudioDur(0);
      lastAudioURL.current = src;
    }
  }, [screen, audioRef, instrOpen]);

  // Track audio progress + auto next
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const onLoaded = () => {
      // Get audio duration for timer calculation
      const duration = audio.duration || 0;
      setAudioDur(duration);
      console.log("Audio loaded, duration:", duration, "seconds");
    };
    const onTime = () => {
      // Update audio position when timeupdate event fires
      const currentTime = audio.currentTime || 0;
      setAudioPos(currentTime);
    };

    // Audio play/pause handlers - removed audio-based timer logic
    const onPlay = () => {
      setAudioStarted(true);
      setTimerPaused(false);
    };
    const onPause = () => {
      setTimerPaused(true);
    };

    const onEnded = () => {
      // Don't auto-advance if timer has expired - let the timer modal handle it
      if (timerExpired) {
        return;
      }

      // In practice mode, don't auto-advance - let user click Next button
      if (mode === "practice") {
        setTimerPaused(true); // Pause timer when audio ends
        return;
      }

      // Simulation mode: auto-advance as before
      // Check if there's a next extract in the current part
      if (listenScreen < playlist.length - 1) {
        // Move to next extract
        setListenScreen((i) => i + 1);
      } else {
        // Move to next part
        onAutoNext?.();
      }
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [
    audioRef,
    listenScreen,
    playlist.length,
    setListenScreen,
    onAutoNext,
    timerExpired,
    mode,
  ]);

  // Simulation: lock seek/pause - only in simulation mode
  useEffect(() => {
    if (mode !== "simulation") return;
    const audio = audioRef?.current;
    if (!audio) return;

    let last = 0;
    const save = () => {
      last = audio.currentTime;
    };
    const snap = () => {
      audio.currentTime = last;
    };
    const keep = () => {
      if (instrOpen) return;
      setTimeout(() => {
        if (!instrOpen) {
          audio.play().catch(() => {});
        }
      }, 50);
    };

    audio.addEventListener("timeupdate", save);
    audio.addEventListener("seeking", snap);
    audio.addEventListener("pause", keep);

    return () => {
      audio.removeEventListener("timeupdate", save);
      audio.removeEventListener("seeking", snap);
      audio.removeEventListener("pause", keep);
    };
  }, [mode, audioRef, instrOpen]);

  // Page part label (fallback to prop)
  const pagePart = screen?.part || currentPart;
  // Patient name for Part A
  const patientName = useMemo(() => {
    if (screen?.part !== "A") return "";
    return screen.patientName || "";
  }, [screen]);
  // Answer state per chip - filter out invalid IDs (NaN, null, undefined)
  const chipStates = headerQuestionIds
    .filter((qid) => qid != null && !isNaN(qid) && typeof qid === "number")
    .map((qid) => ({
      qid,
      answered: hasAnswer(answers, qid),
    }));

  // Check if current screen is the first extract of any part (to hide back button)
  const isFirstExtractOfPart = useMemo(() => {
    if (!screen) return false;

    // For Part A, check if it's the first Part A screen
    if (screen.part === "A") {
      const firstPartAScreen = playlist.findIndex((item) => item.part === "A");
      return listenScreen === firstPartAScreen;
    }

    // For Part B, check if it's the first Part B screen
    if (screen.part === "B") {
      const firstPartBScreen = playlist.findIndex((item) => item.part === "B");
      return listenScreen === firstPartBScreen;
    }

    // For Part C, check if it's the first Part C screen
    if (screen.part === "C") {
      const firstPartCScreen = playlist.findIndex((item) => item.part === "C");
      return listenScreen === firstPartCScreen;
    }

    return false;
  }, [screen, playlist, listenScreen]);

  if (!screen) {
    return <div className="p-8 text-center">No listening data</div>;
  }

  /* ---------------------------------------------------------------
   Gradient Countdown Ring (SVG)
--------------------------------------------------------------- */
  /* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
  const EnhancedCountdown = ({
    size = 56,
    stroke = 5,
    progress = 0, // 0..100
    showPie = true, // show inside pie fill
    showRing = true, // show outer progress ring
    idSuffix = "mm2",
  }) => {
    const p = Math.min(100, Math.max(0, progress));
    const finished = p >= 100;

    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dash = c;
    const offset = c * (1 - p / 100);

    // ------- PIE PATH (sector fill) -------
    const angle = (p / 100) * Math.PI * 2;
    const cx = size / 2;
    const cy = size / 2;
    const x = cx + r * Math.cos(-Math.PI / 2 + angle);
    const y = cy + r * Math.sin(-Math.PI / 2 + angle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const piePath =
      p <= 0
        ? ""
        : `
      M ${cx} ${cy}
      L ${cx} ${cy - r}
      A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
      Z
    `;

    return (
      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#9D2235" />
              <stop offset="100%" stopColor="#5d1220" />
            </linearGradient>
            <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
              <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
            </radialGradient>
          </defs>

          {/* Soft background plate for a card-ish look */}
          <circle cx={cx} cy={cy} r={r} fill="url(#soft-mm2)" />

          {/* Track */}
          {showRing && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="rgba(157,34,53,0.18)"
              strokeWidth={stroke}
            />
          )}

          {/* Pie fill from center (sector) */}
          {showPie && p > 0 && (
            <path d={piePath} fill={`url(#grad-${idSuffix})`} />
          )}

          {/* Progress Ring on top */}
          {showRing && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={`url(#grad-${idSuffix})`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          )}

          {/* Subtle glow when finished */}
          {finished && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#9D2235"
              strokeOpacity="0.25"
              strokeWidth={2}
            />
          )}
        </svg>

        {/* Center icon */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 180ms ease",
            transform: finished ? "scale(1.05)" : "scale(1)",
          }}
        >
          {finished ? (
            <CheckRoundedIcon
              sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
            />
          ) : (
            <AccessTimeIcon
              sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
            />
          )}
        </Box>
      </Box>
    );
  };
  const splitClock = (clock = "00:00:00") => {
    const parts = String(clock).split(":");
    const [hh = "00", mm = "00", ss = "00"] =
      parts.length === 3 ? parts : ["00", "00", "00"];
    return { hh, mm, ss };
  };

  /* ---- Countdown math ---- */
  const getRemainingSecs = () => {
    // Priority: extended 10s “Stay Here”
    if (showExtendedTimer) return Math.max(0, extendedCountdown);

    return Math.max(0, audioTimeRemaining);
  };

  const getTotalSecs = () => {
    // Priority: extended 10s “Stay Here”
    if (showExtendedTimer) return 10;

    return Math.max(1, Math.floor(audioDur + timeExtension) || 1);
  };

  const remainingSecs = getRemainingSecs();
  const totalSecs = getTotalSecs();
  const countdownProgress =
    totalSecs > 0 ? ((totalSecs - remainingSecs) / totalSecs) * 100 : 0;

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* HEADER */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              // Single row layout - all in one line
              gridTemplateAreas: {
                xs: `"left" "center" "right"`, // Stack vertically on mobile
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr", // Single column on mobile
                sm: "1.8fr 1.6fr 1.2fr",
                md: "1.6fr 2fr 1fr",
                lg: "1.8fr 2.2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" }, // Multiple rows on mobile
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 }, // No column gap on mobile
              rowGap: { xs: 0.3, sm: 0 }, // Further reduced row gap on mobile
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 }, // Further reduced mobile height
            }}
          >
            {/* LEFT - Listening Info (column) + Audio (responsive layout) */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile
                alignItems: { xs: "flex-start", sm: "center" },
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 }, // Reduced gap on all screens
                py: { xs: 0.1, sm: 0.2 }, // Further reduced vertical padding
              }}
            >
              {/* LEFT COLUMN - text */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  pr: { xs: 0, sm: 1 }, // No padding on mobile
                  width: { xs: "100%", sm: "auto" }, // Full width on mobile
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "#9D2235",
                    fontSize: { xs: "0.85rem", sm: "1.05rem", md: "1.2rem" },
                    lineHeight: 1.05,
                    whiteSpace: "nowrap",
                  }}
                >
                  Listening Module
                </Typography>

                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    component="span"
                    sx={{
                      display: "inline-block",
                      fontWeight: 800,
                      color: "#222",
                      fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.05rem" },
                      lineHeight: 1.1,
                      letterSpacing: 0.2,
                      textTransform: "none",
                    }}
                  >
                    {screen?.extractName ? (
                      <Box
                        component="span"
                        sx={{
                          bgcolor: "rgba(0,0,0,0.06)",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontWeight: 800,
                          color: "#1f2937",
                        }}
                      >
                        {screen.extractName}
                      </Box>
                    ) : (
                      `Part (${pagePart}) Extract ${
                        partIdx + 1
                      } of ${partTotal}`
                    )}
                  </Typography>
                </Box>
                {/* Summary / group heading (questionMainHeading, range, instructions) */}
                {partAFiltered.summary && (
                  <Box
                    sx={{
                      mt: 1,
                      pl: 0.5,
                      minWidth: 180,
                      maxWidth: { xs: 220, sm: 360 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.25,
                        color: "#9D2235",
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: 1.2,
                      }}
                    >
                      <ArrowRightIcon sx={{ fontSize: 18, mr: 0.5 }} />
                      {partAFiltered.summary.heading}
                    </Box>
                    {partAFiltered.summary.lines?.length ? (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          color: "#1f2937",
                          fontSize: 13,
                          lineHeight: 1.3,
                          "& li": { mb: 0.25, listStyle: "disc" },
                        }}
                      >
                        {partAFiltered.summary.lines.map((txt, i) => (
                          <li key={i}>{txt}</li>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                )}
              </Box>

              {/* RIGHT SIDE - Mobile Responsive Audio Player */}
              <Box
                sx={{
                  ml: { xs: 0, sm: "auto" }, // No auto margin on mobile
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  flexShrink: 0,
                  width: { xs: "100%", sm: "auto" }, // Full width on mobile
                  maxWidth: { xs: "100%", sm: "800px" }, // Increased max width to match audio element
                  gap: 1,
                }}
              >
                <audio
                  ref={audioRef}
                  data-audio-type="unified"
                  controls
                  controlsList="nodownload noplaybackrate noremoteplaybook"
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    width: "100%",
                    maxWidth: "800px", // Significantly increased max width
                    minWidth: "250px", // Increased min width for better mobile experience
                    height: "40px", // Slightly increased height for better visibility
                    outline: "none",
                    pointerEvents: mode === "simulation" ? "none" : "auto",
                    accentColor: "#9D2235",
                    borderRadius: "10px",

                    padding: "3px 6px", // Increased padding for better controls
                    margin: 0,
                    WebkitAppearance: "none",
                    appearance: "none",
                    transition: "all 0.2s",
                  }}
                />

                {/* Playback Speed Control - Only in Practice Mode */}
                {mode === "practice" && (
                  <>
                    <Tooltip title="Playback Speed">
                      <IconButton
                        onClick={(e) => setSpeedMenuAnchor(e.currentTarget)}
                        sx={{
                          bgcolor: "#9D2235",
                          color: "white",
                          width: 40,
                          height: 40,
                          "&:hover": {
                            bgcolor: "#7d1b2a",
                          },
                        }}
                      >
                        <SpeedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={speedMenuAnchor}
                      open={speedMenuOpen}
                      onClose={() => setSpeedMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                        <MenuItem
                          key={speed}
                          onClick={() => {
                            setPlaybackSpeed(speed);
                            setSpeedMenuAnchor(null);
                          }}
                          selected={playbackSpeed === speed}
                          sx={{
                            minWidth: 120,
                            justifyContent: "space-between",
                            "&.Mui-selected": {
                              bgcolor: "rgba(157, 34, 53, 0.08)",
                              "&:hover": {
                                bgcolor: "rgba(157, 34, 53, 0.12)",
                              },
                            },
                          }}
                        >
                          <Typography variant="body2">
                            {speed === 1 ? "Normal" : `${speed}x`}
                          </Typography>
                          {playbackSpeed === speed && (
                            <CheckRoundedIcon
                              sx={{ fontSize: 18, ml: 2, color: "#9D2235" }}
                            />
                          )}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>
            </Box>

            {/* CENTER -  Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              {/* Chips */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {chipStates.map(({ qid, answered }) => (
                    <Box
                      key={qid}
                      onClick={() => handleChipClick(qid)}
                      sx={{
                        width: { xs: 18, sm: 22, md: 26, lg: 30 },
                        height: { xs: 18, sm: 22, md: 26, lg: 30 },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor: answered ? "#9D2235" : "transparent",
                        color: answered ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor: answered
                            ? "#7a182a"
                            : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {qid}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Circle Clock, Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={countdownProgress}
                  showPie
                  showRing
                  idSuffix="headerTime"
                />
              </Box>

              {/* Timer and Notes Row - Below circle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {(() => {
                      const text = showExtendedTimer
                        ? `00:00:${pad2(extendedCountdown)}`
                        : getDisplayTime();
                      return text;
                    })()}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen && setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: { xs: 18, sm: 28, md: 32 },
                      height: { xs: 18, sm: 28, md: 32 },
                    }}
                  >
                    <NoteIcon
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      {/* Body Section */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflowY: "auto",
          bgcolor: "#F7F7F7",
          px: { xs: 1, sm: 1 },
          py: { xs: 1, sm: 1 },
          pb: 0,
        }}
      >
        {/* Content Area */}
        <TextHighlighterWrapper onAddNote={onAddNote}>
          <Box sx={{ width: "100%" }}>
            {screen.type === "noteCompletion" && (
              <Card
                elevation={0}
                sx={{
                  p: { xs: 1, sm: 0 },
                  borderRadius: 2,
                  bgcolor: "#F7F7F7",
                  width: "100%",
                }}
              >
                <MockNoteCompletion
                  extractName={screen.extractName}
                  noteCompletionData={{ sections: partAFiltered.sections }}
                  answers={answers}
                  handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                  dataQuestionIds={partAFiltered.questionIds}
                  instructions={screen.instructions || ""}
                />
              </Card>
            )}

            {screen.type === "composite" && (
              <Box sx={{ width: "100%" }}>
                {/* Render an ordered list of items. Group consecutive items by instruction and show instruction before each group. */}
                {(() => {
                  const items = screen.content?.items || [];
                  const out = [];
                  let noteBuffer = [];
                  let currentInstruction = null;
                  let hasShownNoteInstruction = new Set(); // Track shown instructions for note types by instruction text

                  const flushNotes = () => {
                    if (!noteBuffer.length) return;
                    flushMatchingInfo(); // Flush matching info when flushing notes
                    // Don't flush flowchart here - it causes circular flushing
                    
                    // Separate table_completion from other note types.
                    // If table structure is missing, fallback to regular input rows.
                    const tableNotes = noteBuffer.filter((n) => n.questionType === "table_completion");
                    const tableNotesWithData = tableNotes.filter((n) => n.table);
                    const tableNotesWithoutData = tableNotes.filter((n) => !n.table);
                    const regularNotes = noteBuffer.filter((n) => n.questionType !== "table_completion");
                    const regularNotesWithFallback = [...regularNotes, ...tableNotesWithoutData];
                    
                    // Render regular notes (notes_completion, form_completion, etc.) first
                    if (regularNotesWithFallback.length > 0) {
                      const instructions = regularNotesWithFallback[0]?.instructions || "";
                      const isFormCompletion = regularNotesWithFallback.some((n) => n.questionType === "form_completion");

                      // Group notes by questionMainHeading so each heading block renders separately
                      const byMainHeading = [];
                      regularNotesWithFallback.forEach((n) => {
                        const mh = (n.questionMainHeading || "").trim();
                        const last = byMainHeading[byMainHeading.length - 1];
                        if (last && last.mainHeading === mh) {
                          last.notes.push(n);
                        } else {
                          byMainHeading.push({ mainHeading: mh, notes: [n] });
                        }
                      });

                      byMainHeading.forEach((group, groupIdx) => {
                        const questionMainHeading = group.mainHeading;
                        let sections;
                        if (isFormCompletion) {
                          const allNotes = group.notes.map((n) => ({
                            questionId: n.questionId,
                            text: n.question,
                            hasBlank: n.questionId != null,
                            heading: n.heading,
                            imageUrl: n.imageUrl || null,
                          }));
                          sections = [{ heading: questionMainHeading, notes: allNotes }];
                        } else {
                          const byHeading = new Map();
                          group.notes.forEach((n) => {
                            const h = normalizeHeading(n.heading || "");
                            if (!byHeading.has(h)) byHeading.set(h, []);
                            byHeading.get(h).push({
                              questionId: n.questionId,
                              text: n.question,
                              hasBlank: n.questionId != null,
                              heading: n.heading,
                              imageUrl: n.imageUrl || null,
                            });
                          });
                          sections = Array.from(byHeading.entries()).map(
                            ([heading, notes]) => ({ heading, notes }),
                          );
                        }

                        out.push(
                          <Card
                            key={`notes-${out.length}-${groupIdx}`}
                            elevation={0}
                            sx={{
                              p: { xs: 1, sm: 0 },
                              borderRadius: 2,
                              bgcolor: "#F7F7F7",
                              width: "100%",
                              mb: groupIdx < byMainHeading.length - 1 ? 2 : 0,
                            }}
                          >
                            <MockNoteCompletion
                              extractName={screen.extractName}
                              noteCompletionData={{ sections }}
                              answers={answers}
                              handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                              dataQuestionIds={screen.questionIds || []}
                              questionMainHeading={questionMainHeading}
                              instructions=""
                              tableData={null}
                              questionsMap={{}}
                              isFormCompletion={isFormCompletion}
                            />
                          </Card>,
                        );
                      });
                    }
                    
                    // Render table_completion separately, grouped by table structure
                    if (tableNotes.length > 0) {
                      // Group tableNotes by their table identity (mainHeading + columns)
                      const tableGroups = [];
                      tableNotes.forEach((n) => {
                        const tableKey = n.table
                          ? `${n.table.mainHeading || ""}|||${JSON.stringify(n.table.columns || [])}`
                          : "no-table";
                        const existingGroup = tableGroups.find((g) => g.key === tableKey);
                        if (existingGroup) {
                          existingGroup.notes.push(n);
                        } else {
                          tableGroups.push({ key: tableKey, notes: [n], table: n.table || null });
                        }
                      });

                      tableGroups.forEach((group, groupIdx) => {
                        const questionMainHeading = group.notes[0]?.questionMainHeading || "";
                        const questionsMap = {};
                        group.notes.forEach((n) => {
                          if (n.questionId) {
                            questionsMap[n.questionId] = n.question || "";
                          }
                        });

                        out.push(
                          <Card
                            key={`notes-table-${out.length}-${groupIdx}`}
                            elevation={0}
                            sx={{
                              p: { xs: 1, sm: 0 },
                              borderRadius: 2,
                              bgcolor: "#F7F7F7",
                              width: "100%",
                              mb: groupIdx < tableGroups.length - 1 ? 2 : 0,
                            }}
                          >
                            <MockNoteCompletion
                              extractName={screen.extractName}
                              noteCompletionData={{ sections: [] }}
                              answers={answers}
                              handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                              dataQuestionIds={screen.questionIds || []}
                              questionMainHeading={questionMainHeading}
                              instructions=""
                              tableData={group.table}
                              questionsMap={questionsMap}
                              isFormCompletion={false}
                            />
                          </Card>,
                        );
                      });
                    }
                    
                    noteBuffer = [];
                  };

                  // Track map_labelling questions for grouping by image
                  let mapLabelBuffer = [];
                  let currentMapImage = null;

                  const flushMapLabels = () => {
                    if (!mapLabelBuffer.length) return;
                    flushMatchingInfo(); // Flush matching info when flushing map labels
                    flushMatchingFeature(); // Flush matching feature when flushing map labels
                    // Don't flush flowchart here - it causes circular flushing

                    const questions = [...mapLabelBuffer];
                    const mapImage = currentMapImage;

                    out.push(
                      <Box
                        key={`map-group-${out.length}`}
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "45fr 55fr" },
                            gap: { xs: 2.5, md: 4 },
                            alignItems: "start",
                          }}
                        >
                          {/* Left side - Image */}
                          {mapImage && (
                            <Box
                              sx={{
                                position: { xs: "relative", md: "sticky" },
                                top: { md: 20 },
                                width: "100%",
                                order: 0,
                                bgcolor: "#fafafa",
                                borderRadius: 1.5,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <img
                                src={mapImage}
                                alt="Map"
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  display: "block",
                                  borderRadius: "8px",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                                }}
                              />
                            </Box>
                          )}

                          {/* Right side - Questions */}
                          <Box sx={{ order: 1 }}>
                            {questions.map((mapItem, idx) => (
                              <Box
                                key={`map-${mapItem.questionId}`}
                                data-question-id={mapItem.questionId}
                                sx={{
                                  mb: idx < questions.length - 1 ? 1.5 : 0,
                                }}
                              >
                                <MockMcq
                                  mcqData={mapItem}
                                  selectedValue={
                                    answers[mapItem.questionId] || ""
                                  }
                                  handleAnswerChange={(id, v) =>
                                    onAnswerChange(id, v)
                                  }
                                  asDropdown={true}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>,
                    );

                    mapLabelBuffer = [];
                    currentMapImage = null;
                  };

                  // Track matching_information questions for grouping by instruction
                  let matchingInfoBuffer = [];
                  let currentMatchingInstruction = null;
                  let matchingFeatureBuffer = [];
                  let currentMatchingFeatureInstruction = null;

                  // Track flowchart_completion (text input) questions for grouping
                  let flowchartBuffer = [];
                  let currentFlowchartInstruction = null;

                  const flushFlowchart = () => {
                    if (!flowchartBuffer.length) return;
                    
                    flushMatchingInfo();
                    flushMatchingFeature();

                    const questions = [...flowchartBuffer];

                    // Helper: split question text at underscores for blank rendering
                    const splitAtBlanks = (str) => {
                      const parts = str.split(/_{2,}/);
                      return parts.map((p, i) => ({ text: p, blank: i < parts.length - 1 }));
                    };

                    // Group by questionMainHeading
                    const mainHeading = questions[0]?.questionMainHeading || "";

                    out.push(
                      <Box
                        key={`flowchart-text-group-${out.length}`}
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        {/* Main heading */}
                        {mainHeading && (
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#9D2235",
                              fontSize: "1rem",
                              mb: 2.5,
                              textAlign: "center",
                            }}
                          >
                            {mainHeading}
                          </Typography>
                        )}

                        {/* Flowchart boxes with text inputs */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          {questions.map((flowItem, fIdx) => {
                            const flowItemId = flowItem.questionId;
                            const isLabel = flowItemId == null;
                            const prevItem = fIdx > 0 ? questions[fIdx - 1] : null;
                            const sectionChanged =
                              fIdx === 0 ||
                              (prevItem && (flowItem.heading || "") !== (prevItem.heading || ""));

                            const segments = isLabel ? [] : splitAtBlanks(flowItem.question || "");

                            return (
                              <React.Fragment key={`flow-text-${flowItem.questionId || fIdx}`}>
                                {/* Section heading label */}
                                {sectionChanged && flowItem.heading && (
                                  <Box
                                    sx={{
                                      mb: 1,
                                      mt: fIdx > 0 ? 2 : 0,
                                      px: 2,
                                      py: 0.5,
                                      bgcolor: "#f0f0f0",
                                      borderRadius: 1,
                                      border: "1px solid #ddd",
                                      alignSelf: "stretch",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        color: "#555",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                      }}
                                    >
                                      {flowItem.heading}
                                    </Typography>
                                  </Box>
                                )}

                                {/* Arrow between consecutive items */}
                                {fIdx > 0 && (
                                  <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M 20 5 L 20 30" stroke="#DC143C" strokeWidth="3" strokeLinecap="round" />
                                      <path d="M 20 30 L 15 23 M 20 30 L 25 23" stroke="#DC143C" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                  </Box>
                                )}

                                {/* Label box (null questionId) */}
                                {isLabel ? (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      maxWidth: 600,
                                      p: 1.5,
                                      border: "2px solid #9D2235",
                                      borderRadius: 1.5,
                                      bgcolor: "#fff9f9",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.875rem",
                                        color: "#9D2235",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {flowItem.question}
                                    </Typography>
                                  </Box>
                                ) : (
                                  /* Fill-in box (numbered questionId) with text input */
                                  <Box
                                    data-question-id={flowItemId}
                                    sx={{
                                      width: "100%",
                                      maxWidth: 600,
                                      p: 1.5,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1.5,
                                      bgcolor: "#fff",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#9D2235",
                                          fontSize: "0.875rem",
                                          mr: 0.5,
                                        }}
                                      >
                                        {flowItemId}.
                                      </Typography>
                                      {segments.map((seg, sIdx) => (
                                        <React.Fragment key={sIdx}>
                                          {seg.text && (
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: "0.875rem",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {seg.text}
                                            </Typography>
                                          )}
                                          {seg.blank && (
                                            <TextField
                                              variant="outlined"
                                              size="small"
                                              value={answers?.[flowItemId] || ""}
                                              onChange={(e) =>
                                                onAnswerChange(flowItemId, e.target.value)
                                              }
                                              autoComplete="off"
                                              sx={{
                                                minWidth: 140,
                                                maxWidth: 200,
                                                "& .MuiOutlinedInput-root": {
                                                  borderRadius: 1.5,
                                                  fontWeight: 600,
                                                  fontSize: "0.875rem",
                                                  "& fieldset": { borderColor: "#ccc" },
                                                  "&:hover fieldset": { borderColor: "#9D2235" },
                                                  "&.Mui-focused fieldset": { borderColor: "#9D2235" },
                                                },
                                                "& input": {
                                                  textAlign: "center",
                                                  color: "#9D2235",
                                                  padding: "6px 8px",
                                                },
                                              }}
                                            />
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>,
                    );

                    flowchartBuffer = [];
                    currentFlowchartInstruction = null;
                  };

                  // Track flowchart_completion_select (dropdown) questions for grouping
                  let flowchartSelectBuffer = [];
                  let currentFlowchartSelectInstruction = null;

                  const flushFlowchartSelect = () => {
                    if (!flowchartSelectBuffer.length) return;
                    
                    flushMatchingInfo();
                    flushMatchingFeature();

                    const questions = [...flowchartSelectBuffer];

                    // Get features from the first question's features field
                    const featuresText = questions[0]?.features || "";
                    
                    const featuresLines = featuresText.split("\n").filter(line => line.trim());
                    
                    const firstLineIsOption = featuresLines.length > 0 && /^[A-Z]\s+/.test(featuresLines[0]);
                    
                    const featuresHeading = firstLineIsOption ? "" : (featuresLines[0] || "");
                    const optionLines = firstLineIsOption ? featuresLines : featuresLines.slice(1);
                    
                    const featureOptions = optionLines.map(line => {
                      const match = line.match(/^([A-Z])\s+(.+)$/);
                      return match ? { label: match[1], text: match[2] } : null;
                    }).filter(Boolean);

                    out.push(
                      <Box
                        key={`flowchart-select-group-${out.length}`}
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "45fr 55fr" },
                            gap: { xs: 2.5, md: 4 },
                            alignItems: "start",
                          }}
                        >
                          {/* Left side - Features (if available) */}
                          <Box
                            sx={{
                              position: { xs: "relative", md: "sticky" },
                              top: { md: 20 },
                              width: "100%",
                              order: 0,
                              bgcolor: "#fafafa",
                              borderRadius: 1.5,
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              minHeight: "100px",
                            }}
                          >
                            {featureOptions.length > 0 ? (
                              <>
                                {featuresHeading && (
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#9D2235",
                                      fontSize: "0.95rem",
                                      mb: 1.5,
                                    }}
                                  >
                                    {featuresHeading}
                                  </Typography>
                                )}
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {featureOptions.map((option, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          color: "#9D2235",
                                          fontSize: "0.9rem",
                                          minWidth: "24px",
                                        }}
                                      >
                                        {option.label}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "0.875rem",
                                          lineHeight: 1.5,
                                          color: "#1f2937",
                                        }}
                                      >
                                        {option.text}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </>
                            ) : null}
                          </Box>

                          {/* Right side - Questions with dropdowns */}
                          <Box sx={{ order: 1 }}>
                            {questions.map((flowItem, idx) => {
                              return (
                                <React.Fragment key={`flow-select-${flowItem.questionId}`}>
                                  <Box
                                    data-question-id={flowItem.questionId}
                                    sx={{
                                      mb: idx < questions.length - 1 ? 0 : 0,
                                    }}
                                  >
                                    <MockMcq
                                      mcqData={flowItem}
                                      selectedValue={
                                        answers[flowItem.questionId] || ""
                                      }
                                      handleAnswerChange={(id, v) =>
                                        onAnswerChange(id, v)
                                      }
                                      asDropdown={true}
                                    />
                                  </Box>

                                  {/* Downward arrow between questions */}
                                  {idx < questions.length - 1 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        my: 1.5,
                                      }}
                                    >
                                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path 
                                          d="M 20 5 L 20 30" 
                                          stroke="#DC143C" 
                                          strokeWidth="3" 
                                          strokeLinecap="round"
                                        />
                                        <path 
                                          d="M 20 30 L 15 23 M 20 30 L 25 23" 
                                          stroke="#DC143C" 
                                          strokeWidth="3" 
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </Box>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>,
                    );

                    flowchartSelectBuffer = [];
                    currentFlowchartSelectInstruction = null;
                  };

                  const flushMatchingInfo = () => {
                    if (!matchingInfoBuffer.length) return;
                    // Don't flush matching feature here - let them be independent

                    const questions = [...matchingInfoBuffer];
                    const instruction = currentMatchingInstruction;

                    // Parse features from the first question
                    const featuresText = questions[0]?.features || "";
                    const featuresLines = featuresText.split("\n").filter(line => line.trim());
                    const featuresHeading = featuresLines[0] || "";
                    const featureOptions = featuresLines.slice(1).map(line => {
                      const match = line.match(/^([A-Z])\s+(.+)$/);
                      return match ? { label: match[1], text: match[2] } : null;
                    }).filter(Boolean);

                    // Display instruction if present
                    if (instruction) {
                      out.push(
                        <Paper
                          key={`instruction-matching-info-${out.length}`}
                          elevation={0}
                          sx={{
                            mb: 1.5,
                            mt: out.length > 0 ? 1.5 : 0,
                            p: 1.25,
                            border: `1px solid ${border}`,
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 20, color: "#9D2235", mr: 0.75 }} />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#9D2235",
                                fontSize: "1rem",
                              }}
                            >
                              Instruction
                            </Typography>
                          </Box>
                          <HtmlRenderer
                            content={instruction}
                            component="div"
                            sx={{
                              lineHeight: 1.4,
                              userSelect: "text",
                              cursor: "text",
                              fontSize: "0.8125rem",
                              whiteSpace: "pre-line",
                              "& p": { mb: 0.5 },
                            }}
                          />
                        </Paper>
                      );
                    }

                    out.push(
                      <Box
                        key={`matching-group-${out.length}`}
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "45fr 55fr" },
                            gap: { xs: 2.5, md: 4 },
                            alignItems: "start",
                          }}
                        >
                          {/* Features on the left */}
                          <Box
                            sx={{
                              position: { xgdss: "relative", md: "sticky" },
                              top: { md: 20 },
                              width: "100%",
                              order: 0,
                              bgcolor: "#fafafa",
                              borderRadius: 1.5,
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            {featuresHeading && (
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: "#9D2235",
                                  fontSize: "1rem",
                                  mb: 1.5,
                                }}
                              >
                                {featuresHeading}
                              </Typography>
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {featureOptions.map((option, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      color: "#9D2235",
                                      fontSize: "0.9rem",
                                      minWidth: "24px",
                                    }}
                                  >
                                    {option.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.875rem",
                                      lineHeight: 1.5,
                                      color: "#1f2937",
                                    }}
                                  >
                                    {option.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Questions on the right */}
                          <Box sx={{ order: 1 }}>
                            {questions.map((matchItem, idx) => (
                              <Box
                                key={`matching-${matchItem.questionId}`}
                                data-question-id={matchItem.questionId}
                                sx={{
                                  mb: idx < questions.length - 1 ? 1.5 : 0,
                                }}
                              >
                                <MockMcq
                                  mcqData={matchItem}
                                  selectedValue={
                                    answers[matchItem.questionId] || ""
                                  }
                                  handleAnswerChange={(id, v) =>
                                    onAnswerChange(id, v)
                                  }
                                  asDropdown={true}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>,
                    );

                    matchingInfoBuffer = [];
                    currentMatchingInstruction = null;
                  };

                  const flushMatchingFeature = () => {
                    if (!matchingFeatureBuffer.length) return;

                    const questions = [...matchingFeatureBuffer];
                    const instruction = questions[0]?.instructions || "";

                    // Display instruction if present
                    if (instruction) {
                      out.push(
                        <Paper
                          key={`instruction-matching-feature-${out.length}`}
                          elevation={0}
                          sx={{
                            mb: 1.5,
                            mt: out.length > 0 ? 1.5 : 0,
                            p: 1.25,
                            border: `1px solid ${border}`,
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 20, color: "#9D2235", mr: 0.75 }} />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#9D2235",
                                fontSize: "1rem",
                              }}
                            >
                              Instruction
                            </Typography>
                          </Box>
                          <HtmlRenderer
                            content={instruction}
                            component="div"
                            sx={{
                              lineHeight: 1.4,
                              userSelect: "text",
                              cursor: "text",
                              fontSize: "0.8125rem",
                              whiteSpace: "pre-line",
                              "& p": { mb: 0.5 },
                            }}
                          />
                        </Paper>
                      );
                    }

                    // Parse features from the first question
                    const featuresText = questions[0]?.features || "";
                    const featuresLines = featuresText.split("\n").filter(line => line.trim());
                    const featuresHeading = featuresLines[0] || "";
                    // Parse options supporting both "A. description" and "A description" formats,
                    // and multiple options on the same line (e.g. "A. foo    E. bar")
                    const remainingFeaturesText = featuresLines.slice(1).join(" ");
                    const featureOptions = [];
                    if (remainingFeaturesText) {
                      const splitParts = remainingFeaturesText.split(/([A-H])\.\s+/);
                      // splitParts: ["prefix", "A", "description   ", "B", "description   ", ...]
                      for (let pi = 1; pi < splitParts.length; pi += 2) {
                        const lbl = splitParts[pi];
                        const txt = (splitParts[pi + 1] || "").trim();
                        if (lbl && txt) featureOptions.push({ label: lbl, text: txt });
                      }
                      // Fallback: try "A description" format (no period)
                      if (featureOptions.length === 0) {
                        featuresLines.slice(1).forEach(line => {
                          const match = line.match(/^([A-Z])\s+(.+)$/);
                          if (match) featureOptions.push({ label: match[1], text: match[2] });
                        });
                      }
                    }

                    out.push(
                      <Box
                        key={`matching-feature-group-${out.length}`}
                        sx={{
                          mt: 2,
                          mb: 3,
                          p: { xs: 2, md: 3 },
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "45fr 55fr" },
                            gap: { xs: 2.5, md: 4 },
                            alignItems: "start",
                          }}
                        >
                          {/* Features on the left */}
                          <Box
                            sx={{
                              position: { xs: "relative", md: "sticky" },
                              top: { md: 20 },
                              width: "100%",
                              order: 0,
                              bgcolor: "#fafafa",
                              borderRadius: 1.5,
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            {featuresHeading && (
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: "#9D2235",
                                  fontSize: "1rem",
                                  mb: 1.5,
                                }}
                              >
                                {featuresHeading}
                              </Typography>
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {featureOptions.map((option, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      color: "#9D2235",
                                      fontSize: "0.9rem",
                                      minWidth: "24px",
                                    }}
                                  >
                                    {option.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.875rem",
                                      lineHeight: 1.5,
                                      color: "#1f2937",
                                    }}
                                  >
                                    {option.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Questions on the right */}
                          <Box sx={{ order: 1 }}>
                            {questions.map((matchItem, idx) => (
                              <Box
                                key={`matching-feature-${matchItem.questionId}`}
                                data-question-id={matchItem.questionId}
                                sx={{
                                  mb: idx < questions.length - 1 ? 1.5 : 0,
                                }}
                              >
                                <MockMcq
                                  mcqData={matchItem}
                                  selectedValue={
                                    answers[matchItem.questionId] || ""
                                  }
                                  handleAnswerChange={(id, v) =>
                                    onAnswerChange(id, v)
                                  }
                                  asDropdown={true}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>,
                    );

                    matchingFeatureBuffer = [];
                    currentMatchingFeatureInstruction = null;
                  };

                  items.forEach((item, idx) => {
                    // Handle matching_information questions FIRST - buffer them for grouping
                    if (item.questionType === "matching_information") {
                      flushNotes(); // Flush notes when encountering matching info
                      flushMapLabels(); // Flush map labels when encountering matching info
                      flushFlowchart(); // Flush flowchart when encountering matching info
                      flushFlowchartSelect(); // Flush flowchart select when encountering matching info
                      flushMatchingFeature(); // Flush matching feature when encountering matching info (different type)
                      const itemInstruction = item.instructions || "";

                      // If this is a new instruction or first matching info, start new group
                      if (
                        matchingInfoBuffer.length === 0 ||
                        currentMatchingInstruction === itemInstruction
                      ) {
                        currentMatchingInstruction = itemInstruction;
                        matchingInfoBuffer.push(item);
                      } else {
                        // Different instruction, flush current group and start new one
                        flushMatchingInfo();
                        currentMatchingInstruction = itemInstruction;
                        matchingInfoBuffer.push(item);
                      }
                      return;
                    }

                    // Handle matching_feature questions SECOND - buffer them for grouping by features
                    if (item.questionType === "matching_feature") {
                      flushNotes(); // Flush notes when encountering matching feature
                      flushMapLabels(); // Flush map labels when encountering matching feature
                      flushFlowchart(); // Flush flowchart when encountering matching feature
                      flushFlowchartSelect(); // Flush flowchart select when encountering matching feature
                      flushMatchingInfo(); // Flush matching info when encountering matching feature
                      const itemFeatures = item.features || "";

                      // Group by features (not instruction) - show features once for all questions with same features
                      if (
                        matchingFeatureBuffer.length === 0 ||
                        currentMatchingFeatureInstruction === itemFeatures
                      ) {
                        currentMatchingFeatureInstruction = itemFeatures;
                        matchingFeatureBuffer.push(item);
                      } else {
                        // Different features, flush current group and start new one
                        flushMatchingFeature();
                        currentMatchingFeatureInstruction = itemFeatures;
                        matchingFeatureBuffer.push(item);
                      }
                      return;
                    }

                    // Check if instruction changed - if so, show new instruction block
                    const itemInstruction = item.instructions || "";
                    const itemImage = item.imageUrl || "";
                    const isNoteType = item.type === "note";

                    // For note types (notes_completion), only show instruction once per unique instruction text
                    // For other types (MCQ, map_labelling, etc.), show instruction when it changes
                    const shouldShowInstruction =
                      itemInstruction &&
                      (isNoteType
                        ? !hasShownNoteInstruction.has(itemInstruction) // Only show once per unique instruction for notes
                        : itemInstruction !== currentInstruction); // Show when changed for other types

                    if (shouldShowInstruction) {
                      // Flush any pending notes, map labels, matching info, matching features before showing new instruction
                      // Don't flush flowchart here - let them group across instructions
                      flushNotes();
                      flushMapLabels();
                      flushMatchingInfo();
                      flushMatchingFeature();
                      // Don't flush flowchart - it should group all consecutive flowchart questions

                      // Show instruction for this new group
                      currentInstruction = itemInstruction;
                      if (isNoteType) {
                        hasShownNoteInstruction.add(itemInstruction); // Mark that we've shown this specific instruction for notes
                      }

                      out.push(
                        <Paper
                          key={`instruction-${idx}`}
                          elevation={0}
                          sx={{
                            mb: 1.5,
                            mt: out.length > 0 ? 1.5 : 0,
                            p: 1.25,
                            border: `1px solid ${border}`,
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 20, color: "#9D2235", mr: 0.75 }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#9D2235",
                                fontSize: "1rem",
                              }}
                            >
                              Instruction
                            </Typography>
                          </Box>
                          <HtmlRenderer
                            content={itemInstruction}
                            component="div"
                            sx={{
                              lineHeight: 1.4,
                              userSelect: "text",
                              cursor: "text",
                              fontSize: "0.8125rem",
                              whiteSpace: "pre-line",
                              "& p": { mb: 0.5 },
                            }}
                          />
                        </Paper>,
                      );
                    }

                    if (item.type === "note") {
                      flushMapLabels(); // Flush map labels when encountering notes
                      flushMatchingInfo(); // Flush matching info when encountering notes
                      // Don't flush flowchart - let it group all consecutive flowchart questions
                      noteBuffer.push(item);
                      return;
                    }

                    // Handle map/diagram labelling - buffer them for grouping
                    if (
                      item.type === "map_labelling" ||
                      item.questionType === "map_labelling" ||
                      item.questionType === "diagram_labelling"
                    ) {
                      flushNotes(); // Flush notes when encountering map labels
                      flushMatchingInfo(); // Flush matching info when encountering map labels
                      // Don't flush flowchart - let it group all consecutive flowchart questions
                      const itemImage = item.imageUrl || "";

                      // If this is a new image or first map label, start new group
                      if (
                        mapLabelBuffer.length === 0 ||
                        currentMapImage === itemImage
                      ) {
                        currentMapImage = itemImage;
                        mapLabelBuffer.push(item);
                      } else {
                        // Different image, flush current group and start new one
                        flushMapLabels();
                        currentMapImage = itemImage;
                        mapLabelBuffer.push(item);
                      }
                      return;
                    }

                    // on encountering other types, flush any pending items
                    flushNotes();
                    flushMapLabels();
                    flushMatchingInfo();
                    flushMatchingFeature();
                    // Don't flush flowchart - let it group all consecutive flowchart questions

                    if (
                      item.type === "mcq_single" ||
                      item.type === "mcq_mutiple"
                    ) {
                      // For grouped questions, use first questionId's answer
                      const answerValue =
                        item.isGrouped && item.questionIds?.length > 0
                          ? answers[item.questionIds[0]]
                          : answers[item.questionId];

                      out.push(
                        <Box
                          key={`mcq-${idx}`}
                          data-question-id={item.questionId}
                          sx={{ mt: 2 }}
                        >
                          <MockMcq
                            mcqData={{
                              ...item,
                              ...item.content,
                            }}
                            selectedValue={answerValue || ""}
                            handleAnswerChange={(id, v) =>
                              onAnswerChange(id, v)
                            }
                            asDropdown={item.questionType === "matching_question"}
                          />
                        </Box>,
                      );
                      return;
                    }

                    // Handle flowchart_completion questions (text input) - buffer them for grouping
                    if (item.questionType === "flowchart_completion") {
                      flushNotes();
                      flushMapLabels();
                      flushMatchingInfo();
                      flushMatchingFeature();
                      flushFlowchartSelect();
                      
                      flowchartBuffer.push(item);
                      if (flowchartBuffer.length === 1) {
                        currentFlowchartInstruction = item.instructions || "";
                      }
                      return;
                    }

                    // Handle flowchart_completion_select questions (dropdown) - buffer them for grouping
                    if (item.questionType === "flowchart_completion_select") {
                      flushNotes();
                      flushMapLabels();
                      flushMatchingInfo();
                      flushMatchingFeature();
                      flushFlowchart();
                      
                      flowchartSelectBuffer.push(item);
                      if (flowchartSelectBuffer.length === 1) {
                        currentFlowchartSelectInstruction = item.instructions || "";
                      }
                      return;
                    }

                    // Fallback rendering for unknown types - show basic question text
                    out.push(
                      <Paper
                        key={`other-${idx}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          mt: 2,
                          border: `1px solid ${border}`,
                          borderRadius: 1.5,
                        }}
                      >
                        <HtmlRenderer content={item.question} component="div" />
                      </Paper>,
                    );
                  });

                  // flush any remaining notes, map labels, matching info, matching feature, and flowchart
                  flushNotes();
                  flushMapLabels();
                  flushMatchingInfo();
                  flushMatchingFeature();
                  flushFlowchart();
                  flushFlowchartSelect();
                  return out;
                })()}
              </Box>
            )}

            {screen.type === "mcqSingle" && (
              <Box
                sx={{ width: "100%" }}
                data-question-id={
                  screen.content.questionId ?? screen.content.question_id
                }
              >
                <MockMcq
                  mcqData={screen.content}
                  selectedValue={
                    answers[
                      screen.content.questionId ?? screen.content.question_id
                    ] || ""
                  }
                  handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                  instructions={screen.instructions}
                  partKey="partB"
                  asDropdown={screen.content?.questionType === "matching_question"}
                />
              </Box>
            )}

            {screen.type === "mcqGroup" && (
              <Box sx={{ width: "100%" }}>
                {screen.instructions && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      p: 1.25,
                      border: `1px solid ${border}`,
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 20, color: "#9D2235", mr: 0.75 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#9D2235",
                          fontSize: "1rem",
                        }}
                      >
                        Instruction
                      </Typography>
                    </Box>
                    <HtmlRenderer
                      content={screen.instructions}
                      component="div"
                      sx={{
                        lineHeight: 1.4,
                        userSelect: "text", // Enable text selection
                        cursor: "text", // Show text cursor
                        fontSize: "0.8125rem", // Match Typography body2
                        whiteSpace: "pre-wrap",
                        "& p": { mb: 0.5 },
                      }}
                    />
                  </Paper>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {screen.contents.map((mcq, idx) => (
                    <Box key={idx} data-question-id={mcq.questionId}>
                      <MockMcq
                        mcqData={mcq}
                        selectedValue={answers[mcq.questionId] || ""}
                        handleAnswerChange={(id, v) => onAnswerChange(id, v)}
                        partKey="partC"
                        asDropdown={mcq?.questionType === "matching_question"}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </TextHighlighterWrapper>
      </Box>{" "}
      {/* FOOTER */}
      <Box
        sx={{
          bgcolor: bgPaper,
          borderTop: `1px solid ${divider}`,
          px: { xs: 2, sm: 4 },
          py: 2,
          position: "sticky",
          zIndex: 10,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Back Button - only show in practice mode, not on first screen, and not on first extract of any part */}
        {mode === "practice" &&
          (listenScreen > 0 || onBack) &&
          !isFirstExtractOfPart && (
            <Button
              variant="outlined"
              onClick={() => {
                if (listenScreen > 0) {
                  setListenScreen((prev) => prev - 1);
                } else if (onBack) {
                  onBack();
                }
              }}
              sx={{
                minWidth: 100,
                textTransform: "none",
                borderColor: "#9D2235",
                color: "#9D2235",
                py: 1,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "#9D2235",
                  backgroundColor: "rgba(157, 34, 53, 0.04)",
                },
              }}
            >
              Back
            </Button>
          )}
        {!(
          mode === "practice" &&
          (listenScreen > 0 || onBack) &&
          !isFirstExtractOfPart
        ) && <Box />}

        {/* Continue/Next/Finish Button */}
        <Button
          variant="contained"
          disableElevation
          onClick={handleNextClick}
          sx={{
            minWidth: 140,
            textTransform: "none",
            bgcolor: "#9D2235",
            py: 1,
            borderRadius: 3,
          }}
        >
          {listenScreen < playlist.length - 1
            ? "Continue"
            : mode === "practice" && isLastQuestion
              ? "Finish"
              : mode === "simulation" &&
                  isLastModuleInSimulation &&
                  isLastQuestionInSimulation
                ? "Finish"
                : screen.part === "C"
                  ? "Next Section"
                  : "Next Part"}
        </Button>
      </Box>
      {/* TIMER EXPIRED MODAL */}
      <Dialog open={timerExpired && !expiryHandled}>
        <DialogContent>
          <Typography variant="h6" textAlign="center" gutterBottom>
            Time's up!
          </Typography>
          <Typography textAlign="center" sx={{ mb: 2 }}>
            The test will be submitted in {expiryCountdown} seconds.
          </Typography>
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 48, sm: 72 },
                color: "#9D2235",
              }}
            >
              {expiryCountdown}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: -1 }}>
              seconds
            </Typography>
          </Box>
          <Box
            textAlign="center"
            sx={{ display: "flex", gap: 2, justifyContent: "center" }}
          >
            {!stayHereUsed && (
              <Button
                variant="outlined"
                onClick={handleStayHere}
                sx={{
                  borderColor: "#9D2235",
                  color: "#9D2235",
                  mt: 1,
                  "&:hover": {
                    borderColor: "#7a1a2a",
                    backgroundColor: "rgba(157, 34, 53, 0.04)",
                  },
                }}
              >
                Stay Here
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleTimerContinue}
              sx={{ bgcolor: "#9D2235", mt: 1 }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* CONFIRM NAVIGATION MODAL */}
      <Dialog open={showConfirm} onClose={handleConfirmNo}>
        <DialogContent>
          <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Warning color="warning" sx={{ fontSize: 50 }} />
              <Typography variant="h6" fontWeight="bold">
                Unattempted Question – Confirm Section Completion
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" paragraph>
              Are you sure you want to move to the next section without
              completing question?
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              Are you sure you want to finish this section?
            </Typography>
          </DialogContent>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={handleConfirmNo}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmYes}
              sx={{ bgcolor: "#9D2235" }}
            >
              Yes, Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen && setNotesModalOpen(false)}
        notes={notes}
      />
    </div>
  );
}
